import { ComponentList } from '../../components/core/component-list.js';
import { getFormattedName, getInitialsFromName } from '../../utility/general-utility.js';
import { MapUtils } from '../../utility/map-utils.js';
import { MembershipRole } from './membership-role.js';
import { VersionRange } from '../core/version-range.js';
import { cloneData, cloneDataOrNull, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { PhoneNumber } from './phone-number.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { PartnerAccount } from './partner-account.js';
import { StaffProfessionsComponent } from '../../components/staff-profile/staff-professions-component.js';
import { PartnerProfileBase, PartnerProfileStatus } from '../partner-profile-base.js';
import { fromEnumAsInt, fromEnumAsString, toEnumFromInt, toEnumFromString } from '../../serialization/enum-utils.js';
import { BusinessModuleType } from './business-module-type.js';

export class StaffProfile extends PartnerProfileBase implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '05a0c1ee5eb941829784281eee2da2ac';

  static readonly SCHEMA_VERSION: number = 6;

  public readonly formattedName: string;
  public readonly initials: string;

  constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly phoneNumber: PhoneNumber,
    public readonly photo: PhotoReference | null,
    public readonly userId: string | null,
    public readonly pushNotificationToken: string | null,
    public readonly isPublic: boolean = true,
    public readonly partnerRole: MembershipRole,
    public readonly email: string | null,
    public readonly status: PartnerProfileStatus,
    public readonly components: ComponentList,
  ) {
    super();
    this.formattedName = getFormattedName(firstName, lastName)!;
    this.initials = getInitialsFromName(firstName, lastName)!;
  }

  get $typeId(): string {
    return StaffProfile.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(5, StaffProfile.SCHEMA_VERSION);
  }

  get isDeveloper(): boolean {
    return this.partnerRole === MembershipRole.Developer;
  }

  get isAdministrator(): boolean {
    return this.partnerRole === MembershipRole.Administrator;
  }

  get isEmployee(): boolean {
    return this.partnerRole === MembershipRole.Employee;
  }

  get isNotDeveloper(): boolean {
    return !this.isDeveloper;
  }

  get isNotAdministrator(): boolean {
    return !this.isAdministrator;
  }

  get isNotEmployee(): boolean {
    return !this.isEmployee;
  }

  get isVisibleForClients(): boolean {
    return this.isPublic && this.isNotDeveloper;
  }

  static forOwnerOfBusiness(businessRef: BusinessModuleRef, owner: PartnerAccount): StaffProfile {
    return new StaffProfile(
        owner.uniqueId,
        businessRef.clone(),
        owner.firstName,
        owner.lastName,
        owner.phoneNumber.clone(),
        owner.photo?.clone() ?? null,
        owner.uniqueId,
        owner.pushNotificationToken,
        true,
        MembershipRole.Administrator,
        owner.email,
        PartnerProfileStatus.Live,
        this.createRequiredComponents(businessRef.moduleType),
    );
  }

  static froDeveloperOfBusiness(businessRef: BusinessModuleRef, developer: PartnerAccount): StaffProfile {
    return new StaffProfile(
        developer.uniqueId,
        businessRef.clone(),
        developer.firstName,
        developer.lastName,
        developer.phoneNumber.clone(),
        developer.photo?.clone() ?? null,
        developer.uniqueId,
        developer.pushNotificationToken,
        true,
        MembershipRole.Developer,
        developer.email,
        PartnerProfileStatus.Live,
        this.createRequiredComponents(businessRef.moduleType),
    );
  }

  static createRequiredComponents(moduleType: BusinessModuleType): ComponentList {
    if(moduleType === BusinessModuleType.Calendar) {
      const professionsComponent = new StaffProfessionsComponent([]);
      const result = new ComponentList([professionsComponent]);

      return result;
    }

    return new ComponentList();
  }

  static fromMap(context: SerializationContext, map: SerializedData): StaffProfile {
    return new StaffProfile(
        MapUtils.getString(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
        MapUtils.getString(map, 'firstName'),
        MapUtils.getString(map, 'lastName'),
        PhoneNumber.mustDecode(MapUtils.getString(map, 'phoneNumber')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getRecordOrNull(map, 'photo')),
        MapUtils.getStringOrNull(map, 'userId'),
        MapUtils.getStringOrNull(map, 'pushNotificationToken'),
        MapUtils.getBoolean(map, 'isPublic'),
        toEnumFromInt(MapUtils.getInteger(map, 'partnerRole'), MembershipRole),
        MapUtils.getStringOrNull(map, 'email'),
        toEnumFromString(MapUtils.getString(map, '$status'), PartnerProfileStatus),
        ComponentList.deserialize(context, MapUtils.getRaw(map, 'components') as SerializedData[]),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'firstName': this.firstName,
      'lastName': this.lastName,
      'phoneNumber': this.phoneNumber.encode(),
      'photo': context.toDataOrNull(this.photo),
      'userId': this.userId,
      'partnerRole': fromEnumAsInt(this.partnerRole),
      '$status': fromEnumAsString(this.status),
      'pushNotificationToken': this.pushNotificationToken,
      'isPublic': this.isPublic,
      'email': this.email,
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = cloneData(this.businessRef),
    firstName = this.firstName,
    lastName = this.lastName,
    phoneNumber = cloneData(this.phoneNumber),
    photo = cloneDataOrNull(this.photo),
    userId = this.userId,
    pushNotificationToken = this.pushNotificationToken,
    isPublic = this.isPublic,
    partnerRole = this.partnerRole,
    email = this.email,
    status = this.status,
    components = cloneData(this.components),
  } : {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    firstName?: string,
    lastName?: string,
    phoneNumber?: PhoneNumber,
    photo?: PhotoReference | null,
    userId?: string | null,
    pushNotificationToken?: string | null,
    isPublic?: boolean,
    partnerRole?: MembershipRole,
    email?: string | null,
    status?: PartnerProfileStatus,
    components?: ComponentList,
  } = {}): this {
    return new StaffProfile(
        uniqueId,
        businessRef,
        firstName,
        lastName,
        phoneNumber,
        photo,
        userId,
        pushNotificationToken,
        isPublic,
        partnerRole,
        email,
        status,
        components,
    ) as this;
  }

  isSame(other: StaffProfile): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.businessRef === this.businessRef &&
        other.firstName === this.firstName &&
        other.lastName === this.lastName &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        isSameData(other.photo, this.photo) &&
        other.userId === this.userId &&
        other.partnerRole === this.partnerRole &&
        other.status === this.status &&
        other.pushNotificationToken === this.pushNotificationToken &&
        other.isPublic === this.isPublic &&
        other.email === this.email &&
        other.components.isSame(this.components);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new StaffProfile(
        this.uniqueId,
        businessRef.clone(),
        this.firstName,
        this.lastName,
        this.phoneNumber.clone(),
        this.photo?.clone() ?? null,
        this.userId,
        this.pushNotificationToken,
        this.isPublic,
        this.partnerRole,
        this.email,
        this.status,
        this.components.copyWithBusinessRef(businessRef),
    ) as this;
  }
}

export class StaffProfileBuilder extends DataModelBuilder<StaffProfile> {
  build(context: SerializationContext, map: SerializedData): StaffProfile {
    return StaffProfile.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(StaffProfile.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(StaffProfile.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [components] of type [ComponentList] was introduced.
    if(version < 2) {
      MapUtils.set(map, 'component', []);
      version = 2;
    }

    // In this version [businessId] was replaced with [businessRef]
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 3;
    }

    // In this version the model was converted [StaffProfileBase] and
    // `userId` of type `string` was converted to `string | null`.
    if(version < 4) {
      version = 4;
    }

    // In this version the `userRole` field was replaced with `partnerRole`.
    if(version < 5) {
      version = 5;
      map['partnerRole'] = map['userRole'];
    }

    // In this version the `$status` field was added
    if(version < 6) {
      MapUtils.setIfNullOrUndefined(map, '$status', fromEnumAsString(PartnerProfileStatus.Live));
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  // Never remove this backwards migration, otherwise the mobile apps will break.
  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: StaffProfile,
      map: SerializedData
  ): SerializedData {
    const versionRange = MapUtils.getVersionRange(map);
    let version = versionRange.from;

    if(version >= 5) {
      version = 3;
      map['userRole'] = map['partnerRole'];
      map['$schema_version'] = VersionRange.encodedBetween(3, 6);
    }

    return map;
  }
}
